import React, { useCallback, useMemo, useRef, useState } from 'react'

import { BriefcaseBusiness, type LucideProps } from 'lucide-react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import type { IconType } from 'react-icons'
import { BsFileEarmarkWord } from 'react-icons/bs'
import {
  LuBookMarked,
  LuBox,
  LuBuilding,
  LuBuilding2,
  LuChevronDown,
  LuStickyNote,
  LuFiles,
  LuFolderOpen,
  LuGlobe,
  LuHome,
  LuPlus,
  LuTag,
  LuUsers,
} from 'react-icons/lu'
import { SiMicrosoftsharepoint } from 'react-icons/si'
import { TbBlockquote } from 'react-icons/tb'
import { twMerge } from 'tailwind-merge'

import Avatar from '@/components/common/Avatar'
import { Badge } from '@/components/common/BadgeV2'
import Button from '@/components/common/Button'
import { Deprecated } from '@/components/common/Deprecated'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/common/DropdownMenu'
import { SharePointIcon as BaseSharePointIcon } from '@/components/common/LogoIcon'
import { OrgPermissionGate } from '@/components/common/PermissionGate'
import { AvatarSkeleton, Skeleton } from '@/components/common/Skeleton'
import Spinner from '@/components/common/Spinner'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/common/TooltipV3'
import { useActiveCatalogSchemaId } from '@/components/library-v2/catalog/hooks'
import { CreateCompanyDialog } from '@/components/library-v2/companies/CreateCompanyDialog'
import { CreateFromWebsiteDialog } from '@/components/library-v2/files/CreateFromWebsiteDialog'
import { CreatePersonDialog } from '@/components/library-v2/people/CreatePersonDialog'
import { useProfileSidebarState } from '@/components/library-v2/ui/Sidebar/hooks'
import { UploadFileDialog } from '@/components/library/dashboard/files/UploadFileDialog'
import { EditTagsDialog } from '@/components/tags/EditTagsDialog'
import { UploadTemplateDialog } from '@/components/word-templates/UploadTemplateDialog'

import { formatNumberWithSuffix } from '@/helpers/string'
import { getFaviconUrl } from '@/helpers/url'

import { useCanAccessPremium } from '@/hooks/billing/useCanAccessPremium'
import { useCreateExcerpt } from '@/hooks/excerpts/useCreateExcerpt'
import { useExcerptCount } from '@/hooks/excerpts/useExcerpts'
import { useSuggestedCompaniesCount } from '@/hooks/library/useCompanies'
import { useCompanyCount } from '@/hooks/library/useCompanyCount'
import {
  type OrgCompanyProfileResponse,
  useOrgCompanyProfile,
  useOrgCompanyProfileId,
} from '@/hooks/library/useOrgCompanyProfile'
import { useSuggestedPeopleCount } from '@/hooks/library/usePeople'
import { usePeopleCount } from '@/hooks/library/usePeopleCount'
import { useMemoryCount } from '@/hooks/memories/useMemories'
import { useSafePush } from '@/hooks/navigation/useSafePush'
import { useActiveOrgId } from '@/hooks/organization/useActiveOrgId'
import { useOrganization } from '@/hooks/organization/useOrganization'
import { useOrganizationFilesCount } from '@/hooks/organization/useOrganizationFiles'
import { usePromptCount } from '@/hooks/prompts'
import { useSharepointSyncedFilesCount } from '@/hooks/sharepoint/useSharepointSyncedFilesCount'
import { useCreateTemplate } from '@/hooks/templates/useCreateTemplate'
import { useTemplateCount } from '@/hooks/templates/useTemplates'
import { useWordTemplatesCount } from '@/hooks/word-templates/useWordTemplates'

import type { File } from '@/types/File'
import type { Organization } from '@/types/Organization'

export type LibrarySidebarId =
  | 'home'
  | 'profile'
  | 'files'
  | 'sharepoint'
  | 'templates'
  | 'word-templates'
  | 'excerpts'
  | 'prompts'
  | 'memories'
  | 'people'
  | 'companies'
  | 'agencies'
  | 'catalog'

// Alias for internal use
type SidebarId = LibrarySidebarId

export type LibraryIconColor =
  | 'accent-light'
  | 'zinc-light'
  | 'blue-light'
  | 'indigo-light'
  | 'fuchsia-light'
  | 'green-light'
  | 'purple-light'
  | 'pink-light'
  | 'amber-light'
  | 'yellow-light'
  | 'red-light'

// Alias for internal use
type IconColor = LibraryIconColor

interface NavigationChildItem {
  id: string
  label: string
  route: string
  count: number | null
  active: boolean
}

type Icon =
  | IconType
  | React.ForwardRefExoticComponent<
      Omit<LucideProps, 'ref'> & React.RefAttributes<SVGSVGElement>
    >

interface NavigationItem {
  icon: Icon | React.ReactNode
  id: SidebarId
  label: string
  count: number | null
  suggestionCount?: number | null
  suggestionRoute?: string
  color: IconColor
  route: string
  children?: NavigationChildItem[]
  onNew?: () => void
  /** Override the default active state calculation */
  isActiveOverride?: boolean
}

export const LIBRARY_SIDEBAR_ID_TO_COLOR: Record<SidebarId, IconColor> = {
  home: 'zinc-light',
  profile: 'zinc-light',
  files: 'zinc-light',
  sharepoint: 'zinc-light',
  templates: 'purple-light',
  'word-templates': 'blue-light',
  excerpts: 'fuchsia-light',
  prompts: 'zinc-light',
  memories: 'zinc-light',
  people: 'green-light',
  companies: 'yellow-light',
  agencies: 'purple-light',
  catalog: 'red-light',
}

export const LIBRARY_SIDEBAR_ID_TO_ICON: Record<SidebarId, Icon> = {
  home: LuHome,
  profile: BriefcaseBusiness,
  files: LuFolderOpen,
  sharepoint: SiMicrosoftsharepoint,
  templates: LuFiles,
  'word-templates': BsFileEarmarkWord,
  excerpts: TbBlockquote,
  prompts: LuBookMarked,
  memories: LuStickyNote,
  people: LuUsers,
  companies: LuBuilding2,
  agencies: LuBuilding,
  catalog: LuBox,
}

export const LIBRARY_ICON_BG: Record<IconColor, string> = {
  'accent-light': 'bg-accent-200 dark:bg-accent-500/40',
  'zinc-light': 'bg-zinc-200 dark:bg-zinc-500/40',
  'blue-light': 'bg-blue-100 dark:bg-blue-500/40',
  'indigo-light': 'bg-indigo-100 dark:bg-indigo-500/40',
  'fuchsia-light': 'bg-fuchsia-100 dark:bg-fuchsia-500/40',
  'green-light': 'bg-green-100 dark:bg-green-500/40',
  'purple-light': 'bg-purple-100 dark:bg-purple-500/40',
  'yellow-light': 'bg-yellow-100 dark:bg-yellow-500/40',
  'pink-light': 'bg-pink-100 dark:bg-pink-500/40',
  'amber-light': 'bg-amber-100 dark:bg-amber-500/40',
  'red-light': 'bg-red-100 dark:bg-red-500/40',
}

export const LIBRARY_ICON_TEXT: Record<IconColor, string> = {
  'accent-light': 'text-accent-600 dark:text-accent-300',
  'zinc-light': 'text-zinc-600 dark:text-zinc-300',
  'blue-light': 'text-blue-600 dark:text-blue-300',
  'indigo-light': 'text-indigo-600 dark:text-indigo-300',
  'fuchsia-light': 'text-fuchsia-600 dark:text-fuchsia-300',
  'green-light': 'text-green-600 dark:text-green-300',
  'purple-light': 'text-purple-600 dark:text-purple-300',
  'yellow-light': 'text-yellow-600 dark:text-yellow-300',
  'pink-light': 'text-pink-600 dark:text-pink-300',
  'amber-light': 'text-amber-600 dark:text-amber-300',
  'red-light': 'text-red-600 dark:text-red-300',
}

export const LIBRARY_ICON_BORDER: Record<IconColor, string> = {
  'accent-light': 'ring-1 ring-accent-300 dark:ring-accent-500/50',
  'zinc-light': 'ring-1 ring-zinc-300 dark:ring-zinc-500/50',
  'blue-light': 'ring-1 ring-blue-300 dark:ring-blue-500/50',
  'indigo-light': 'ring-1 ring-indigo-300 dark:ring-indigo-500/50',
  'fuchsia-light': 'ring-1 ring-fuchsia-300 dark:ring-fuchsia-500/50',
  'green-light': 'ring-1 ring-green-300 dark:ring-green-500/50',
  'purple-light': 'ring-1 ring-purple-300 dark:ring-purple-500/50',
  'yellow-light': 'ring-1 ring-yellow-300 dark:ring-yellow-500/50',
  'pink-light': 'ring-1 ring-pink-300 dark:ring-pink-500/50',
  'amber-light': 'ring-1 ring-amber-300 dark:ring-amber-500/50',
  'red-light': 'ring-1 ring-red-300 dark:ring-red-500/50',
}

// Hover variants for group-hover effects
export const LIBRARY_ICON_BG_HOVER: Record<IconColor, string> = {
  'accent-light': 'group-hover:bg-accent-200 dark:group-hover:bg-accent-500/40',
  'zinc-light': 'group-hover:bg-zinc-200 dark:group-hover:bg-zinc-500/40',
  'blue-light': 'group-hover:bg-blue-100 dark:group-hover:bg-blue-500/40',
  'indigo-light': 'group-hover:bg-indigo-100 dark:group-hover:bg-indigo-500/40',
  'fuchsia-light':
    'group-hover:bg-fuchsia-100 dark:group-hover:bg-fuchsia-500/40',
  'green-light': 'group-hover:bg-green-100 dark:group-hover:bg-green-500/40',
  'purple-light': 'group-hover:bg-purple-100 dark:group-hover:bg-purple-500/40',
  'yellow-light': 'group-hover:bg-yellow-100 dark:group-hover:bg-yellow-500/40',
  'pink-light': 'group-hover:bg-pink-100 dark:group-hover:bg-pink-500/40',
  'amber-light': 'group-hover:bg-amber-100 dark:group-hover:bg-amber-500/40',
  'red-light': 'group-hover:bg-red-100 dark:group-hover:bg-red-500/40',
}

export const LIBRARY_ICON_TEXT_HOVER: Record<IconColor, string> = {
  'accent-light':
    'group-hover:text-accent-600 dark:group-hover:text-accent-300',
  'zinc-light': 'group-hover:text-zinc-600 dark:group-hover:text-zinc-300',
  'blue-light': 'group-hover:text-blue-600 dark:group-hover:text-blue-300',
  'indigo-light':
    'group-hover:text-indigo-600 dark:group-hover:text-indigo-300',
  'fuchsia-light':
    'group-hover:text-fuchsia-600 dark:group-hover:text-fuchsia-300',
  'green-light': 'group-hover:text-green-600 dark:group-hover:text-green-300',
  'purple-light':
    'group-hover:text-purple-600 dark:group-hover:text-purple-300',
  'yellow-light':
    'group-hover:text-yellow-600 dark:group-hover:text-yellow-300',
  'pink-light': 'group-hover:text-pink-600 dark:group-hover:text-pink-300',
  'amber-light': 'group-hover:text-amber-600 dark:group-hover:text-amber-300',
  'red-light': 'group-hover:text-red-600 dark:group-hover:text-red-300',
}

export const LIBRARY_ICON_BORDER_HOVER: Record<IconColor, string> = {
  'accent-light':
    'group-hover:ring-1 group-hover:ring-accent-300/80 dark:group-hover:ring-accent-500/50',
  'zinc-light':
    'group-hover:ring-1 group-hover:ring-zinc-200 dark:group-hover:ring-zinc-500/50',
  'blue-light':
    'group-hover:ring-1 group-hover:ring-blue-200 dark:group-hover:ring-blue-500/50',
  'indigo-light':
    'group-hover:ring-1 group-hover:ring-indigo-200 dark:group-hover:ring-indigo-500/50',
  'fuchsia-light':
    'group-hover:ring-1 group-hover:ring-fuchsia-200 dark:group-hover:ring-fuchsia-500/50',
  'green-light':
    'group-hover:ring-1 group-hover:ring-green-200 dark:group-hover:ring-green-500/50',
  'purple-light':
    'group-hover:ring-1 group-hover:ring-purple-200 dark:group-hover:ring-purple-500/50',
  'yellow-light':
    'group-hover:ring-1 group-hover:ring-yellow-200 dark:group-hover:ring-yellow-500/50',
  'pink-light':
    'group-hover:ring-1 group-hover:ring-pink-200 dark:group-hover:ring-pink-500/50',
  'amber-light':
    'group-hover:ring-1 group-hover:ring-amber-200 dark:group-hover:ring-amber-500/50',
  'red-light':
    'group-hover:ring-1 group-hover:ring-red-200 dark:group-hover:ring-red-500/50',
}

const isActive = (id: SidebarId, route: string) => {
  if (id === 'home') {
    return typeof window !== 'undefined' && window.location.pathname === route
  }
  return (
    typeof window !== 'undefined' && window.location.pathname.includes(route)
  )
}

interface SidebarItemProps {
  item: NavigationItem
  className?: string
}

const SidebarItem: React.FC<SidebarItemProps> = ({ item, className }) => {
  const [expanded, setExpanded] = useState<boolean>(true)
  const router = useRouter()

  // Use isActiveOverride if provided, otherwise use default active state calculation
  const active =
    item.isActiveOverride !== undefined
      ? item.isActiveOverride
      : (item.route ? isActive(item.id, item.route) : false) ||
        (item.children && item.children.some((child) => child.active))
  const hasChildren = item.children && item.children.length > 0

  const Icon = item.icon

  const canExpand = (item.children && item.children.length > 0) || item.onNew

  const iconIsReactNode = typeof Icon !== 'function'

  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      if (item.children && item.children.length > 0) {
        event.preventDefault()
        setExpanded((prev) => !prev)
      }
    },
    [item.children, setExpanded],
  )

  const handleSuggestionClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (!item.suggestionRoute) return
      event.stopPropagation()
      void router.push(item.suggestionRoute)
    },
    [item.suggestionRoute, router],
  )

  const suggestionTooltipText =
    item.suggestionCount && item.suggestionCount > 0
      ? item.suggestionRoute
        ? `${item.suggestionCount.toLocaleString()} pending. Click to review and confirm.`
        : `${item.suggestionCount.toLocaleString()} pending.`
      : ''

  return (
    <div
      className={twMerge(canExpand ? 'space-y-1.5' : 'space-y-0', className)}
    >
      <div
        className={twMerge(
          'group/item w-full flex items-center justify-between rounded-[7px] text-primary-700 dark:text-primary-300 truncate',
          'transition-all duration-50',
          active
            ? 'bg-primary-200/60 dark:bg-primary-800'
            : 'hover:bg-primary-100 hover:text-primary-900 dark:hover:bg-primary-800 dark:hover:text-primary-100',
          className,
        )}
      >
        {item.children ? (
          <button
            type="button"
            onClick={handleClick}
            className="min-w-0 flex flex-1 items-center justify-between gap-2 p-[5px] text-left"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={twMerge(
                  iconIsReactNode ? '' : LIBRARY_ICON_BG[item.color],
                  iconIsReactNode ? '' : LIBRARY_ICON_TEXT[item.color],
                  'border-0',
                  'w-7 h-7 rounded-md flex items-center justify-center',
                )}
              >
                {iconIsReactNode ? Icon : <Icon size={16} />}
              </div>
              <span className={twMerge('font-medium text-sm truncate')}>
                {item.label}
              </span>
            </div>
            {(hasChildren || item.onNew) && (
              <LuChevronDown
                size={15}
                className={twMerge(
                  'text-primary-500 dark:text-primary-400',
                  'transition-opacity duration-150',
                  'group-hover/item:opacity-100 opacity-0',
                  expanded ? 'rotate-180 opacity-100' : '',
                  // if this chevron has no siblings, add some margin to the right to make the alignment look better
                  (!item.suggestionCount || item.suggestionCount === 0) &&
                    (item.count === null || item.count === 0)
                    ? 'mr-2'
                    : '',
                )}
              />
            )}
          </button>
        ) : (
          <Link
            href={item.route ?? '#'}
            onClick={handleClick}
            className="min-w-0 flex flex-1 items-center justify-between gap-2 p-[5px] text-left"
          >
            <div className="flex min-w-0 items-center gap-3">
              <div
                className={twMerge(
                  iconIsReactNode ? '' : LIBRARY_ICON_BG[item.color],
                  iconIsReactNode ? '' : LIBRARY_ICON_TEXT[item.color],
                  'border-0',
                  'w-7 h-7 rounded-md flex items-center justify-center',
                )}
              >
                {iconIsReactNode ? Icon : <Icon size={16} />}
              </div>
              <span className={twMerge('font-medium text-sm truncate')}>
                {item.label}
              </span>
            </div>
            {(hasChildren || item.onNew) && (
              <LuChevronDown
                size={15}
                className={twMerge(
                  'text-primary-500 dark:text-primary-400',
                  'transition-opacity duration-150',
                  'group-hover/item:opacity-100 opacity-0',
                  expanded ? 'rotate-180 opacity-100' : '',
                  // if this chevron has no siblings, add some margin to the right to make the alignment look better
                  (!item.suggestionCount || item.suggestionCount === 0) &&
                    (item.count === null || item.count === 0)
                    ? 'mr-2'
                    : '',
                )}
              />
            )}
          </Link>
        )}
        <div className="flex flex-shrink-0 items-center gap-2 pr-[5px]">
          {item.suggestionCount && item.suggestionCount > 0 && (
            <Tooltip delayDuration={200}>
              <TooltipTrigger asChild>
                {item.suggestionRoute ? (
                  <button
                    type="button"
                    aria-label={`Review ${item.suggestionCount} pending ${item.label.toLowerCase()}`}
                    onClick={handleSuggestionClick}
                    className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full hover:bg-amber-100 dark:hover:bg-amber-500/20"
                  >
                    <span
                      aria-hidden="true"
                      className="w-[9px] h-[9px] bg-amber-500 rounded-full"
                    />
                  </button>
                ) : (
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                    <span className="w-[9px] h-[9px] bg-amber-500 rounded-full" />
                  </span>
                )}
              </TooltipTrigger>
              <TooltipContent
                side="right"
                sideOffset={12}
                className="max-w-[356px]"
              >
                <p>{suggestionTooltipText}</p>
              </TooltipContent>
            </Tooltip>
          )}
          {item.count !== null && (
            <Badge
              variant="outline"
              className="py-[2px] text-center text-xs min-w-[30px]"
            >
              {formatNumberWithSuffix(item.count, 1)}
            </Badge>
          )}
        </div>
      </div>
      {expanded && (
        <div className="flex flex-col flex-1 gap-1.5 h-full ml-[19px] border-l border-primary-200 dark:border-primary-700">
          {item.children && item.children.length > 0 && (
            <div className="flex flex-col gap-1.5 w-full pl-[8px]">
              {item.children.map((child, index) => (
                <Link
                  key={index}
                  href={child.route}
                  className={twMerge(
                    'text-sm text-primary-600 dark:text-primary-300 rounded-md pl-[18px] pr-3 py-1.5 transition-colors duration-100',
                    'truncate',
                    child.active
                      ? 'bg-primary-100 dark:bg-primary-800 text-primary-900 dark:text-primary-100'
                      : 'hover:bg-primary-100 hover:text-primary-900 dark:hover:bg-primary-800 dark:hover:text-primary-100',
                  )}
                >
                  <span className="truncate">{child.label}</span>
                </Link>
              ))}
            </div>
          )}
          {item.onNew && (
            <div className="pl-[8px]">
              <OrgPermissionGate permission="write" fallback="disable">
                <NewItem onNew={item.onNew} label="Import Catalog" />
              </OrgPermissionGate>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface NewItemProps {
  onNew: () => void
  label?: string
  className?: string
  disabled?: boolean
}

const NewItem: React.FC<NewItemProps> = ({
  onNew,
  label = 'New',
  className,
  disabled = false,
}) => {
  return (
    <button
      onClick={onNew}
      disabled={disabled}
      className={twMerge(
        'flex items-center justify-between gap-1',
        'text-sm text-blue-600 dark:text-blue-400 rounded-md pl-[18px] pr-[13px] py-1.5 transition-colors duration-100 text-left w-full',
        disabled
          ? 'cursor-not-allowed opacity-60'
          : 'hover:bg-blue-50 hover:text-blue-700 dark:hover:bg-blue-800/40 dark:hover:text-blue-300',
        className,
      )}
    >
      <span>{label}</span>
      <LuPlus size={14} className="flex-shrink-0" />
    </button>
  )
}

const FilesIcon = LIBRARY_SIDEBAR_ID_TO_ICON['files']
const TemplatesIcon = LIBRARY_SIDEBAR_ID_TO_ICON['templates']
const WordTemplatesIcon = LIBRARY_SIDEBAR_ID_TO_ICON['word-templates']
const ExcerptsIcon = LIBRARY_SIDEBAR_ID_TO_ICON['excerpts']
const PeopleIcon = LIBRARY_SIDEBAR_ID_TO_ICON['people']
const CompaniesIcon = LIBRARY_SIDEBAR_ID_TO_ICON['companies']

const BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME =
  'w-6 h-6 rounded-[5px] flex items-center justify-center flex-shrink-0'

interface NewButtonProps {
  /**
   * Hands "Upload File" to a host that mounts its own upload dialog. Without
   * it the sidebar mounts one; with it a page would show two.
   */
  onUploadFile?: () => void
}

const NewButton: React.FC<NewButtonProps> = ({ onUploadFile }) => {
  const buttonRef = useRef<HTMLButtonElement>(null)

  const { activeOrgId } = useActiveOrgId()
  const { safePush } = useSafePush()
  const { canAccessPremium, isLoading: isLoadingPremium } =
    useCanAccessPremium()

  const router = useRouter()

  const [uploadFileDialogOpen, setUploadFileDialogOpen] =
    useState<boolean>(false)
  const [uploadTemplateDialogOpen, setUploadTemplateDialogOpen] =
    useState<boolean>(false)
  const [createFromWebsiteDialogOpen, setCreateFromWebsiteDialogOpen] =
    useState<boolean>(false)
  const [createPersonDialogOpen, setCreatePersonDialogOpen] =
    useState<boolean>(false)
  const [createCompanyDialogOpen, setCreateCompanyDialogOpen] =
    useState<boolean>(false)
  const [createTagDialogOpen, setCreateTagDialogOpen] = useState<boolean>(false)

  const { mutateAsync: createExcerpt, isPending: isCreatingExcerpt } =
    useCreateExcerpt({ orgId: activeOrgId })

  const { mutateAsync: createTemplate, isPending: isCreatingTemplate } =
    useCreateTemplate({ orgId: activeOrgId })

  const anyPending = isCreatingExcerpt || isCreatingTemplate

  const handleCreateExcerpt = useCallback(async () => {
    await createExcerpt(
      { name: 'Untitled excerpt', unrestricted: true },
      {
        onSuccess: (excerpt) => {
          safePush(`/orgs/${activeOrgId}/library/excerpts/${excerpt.id}`)
        },
      },
    )
  }, [activeOrgId, safePush, createExcerpt])

  const handleCreateTemplate = useCallback(async () => {
    await createTemplate(
      { name: 'Untitled template', unrestricted: true },
      {
        onSuccess: (template) => {
          safePush(`/orgs/${activeOrgId}/library/templates/${template.id}`)
        },
      },
    )
  }, [activeOrgId, safePush, createTemplate])

  const handleUploadFileSuccess = useCallback(
    (files: File[]) => {
      // Get current pathname without query parameters
      const currentPath = router.asPath.split('?')[0]
      const filesListPath = `/orgs/${activeOrgId}/library/files`

      if (files.length > 1) {
        // Only navigate if not currently on the files list page
        if (currentPath !== filesListPath) {
          safePush(filesListPath)
        }
      } else if (files.length === 1) {
        const filePath = `/orgs/${activeOrgId}/library/files/${files[0].id}`
        // Only navigate if currently viewing another file
        if (currentPath !== filePath) {
          safePush(filePath)
        }
      }
    },
    [activeOrgId, safePush, router.asPath],
  )

  const handleUploadFile = useCallback(() => {
    if (onUploadFile) {
      onUploadFile()
      return
    }
    setUploadFileDialogOpen(true)
  }, [onUploadFile])

  const handleCreateFromWebsite = () => setCreateFromWebsiteDialogOpen(true)

  const handleUploadTemplate = useCallback(() => {
    setUploadTemplateDialogOpen(true)
  }, [setUploadTemplateDialogOpen])

  const handleCreatePerson = useCallback(() => {
    setCreatePersonDialogOpen(true)
  }, [setCreatePersonDialogOpen])

  const handleCreateTag = useCallback(() => {
    setCreateTagDialogOpen(true)
  }, [setCreateTagDialogOpen])

  const handleCreateCompany = useCallback(() => {
    setCreateCompanyDialogOpen(true)
  }, [setCreateCompanyDialogOpen])

  return (
    <>
      {onUploadFile ? null : (
        <UploadFileDialog
          showAutoExtractEntities={true}
          open={uploadFileDialogOpen}
          setOpen={setUploadFileDialogOpen}
          onSuccess={handleUploadFileSuccess}
        />
      )}
      <CreateFromWebsiteDialog
        open={createFromWebsiteDialogOpen}
        setOpen={setCreateFromWebsiteDialogOpen}
      />
      <UploadTemplateDialog
        open={uploadTemplateDialogOpen}
        setOpen={setUploadTemplateDialogOpen}
      />
      <CreatePersonDialog
        open={createPersonDialogOpen}
        setOpen={setCreatePersonDialogOpen}
      />
      <CreateCompanyDialog
        open={createCompanyDialogOpen}
        setOpen={setCreateCompanyDialogOpen}
      />
      <EditTagsDialog
        open={createTagDialogOpen}
        setOpen={setCreateTagDialogOpen}
        createNewTagOnOpen={true}
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild={true} className="w-full">
          <Button
            ref={buttonRef}
            variant="primary-compact"
            className="w-full active:scale-100"
            disabled={anyPending}
            isLoading={anyPending}
          >
            <LuPlus size={14} className="flex-shrink-0" />
            <span>Add</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent
          className="w-full"
          style={{
            width: `${Math.min(220, (buttonRef.current?.clientWidth ?? 200) + 2)}px`,
          }}
        >
          <OrgPermissionGate permission="write" fallback="disable" asChild>
            <DropdownMenuItem
              className="flex items-center gap-2.5"
              onClick={handleUploadFile}
            >
              <div
                className={twMerge(
                  LIBRARY_ICON_BG[LIBRARY_SIDEBAR_ID_TO_COLOR['files']],
                  LIBRARY_ICON_TEXT[LIBRARY_SIDEBAR_ID_TO_COLOR['files']],
                  BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
                )}
              >
                <FilesIcon size={16} />
              </div>
              <span className="text-sm">Upload File</span>
            </DropdownMenuItem>
          </OrgPermissionGate>
          <OrgPermissionGate permission="write" fallback="disable" asChild>
            <DropdownMenuItem
              className="flex items-center gap-2.5"
              onClick={handleCreateFromWebsite}
            >
              <div
                className={twMerge(
                  LIBRARY_ICON_BG[LIBRARY_SIDEBAR_ID_TO_COLOR['files']],
                  LIBRARY_ICON_TEXT[LIBRARY_SIDEBAR_ID_TO_COLOR['files']],
                  BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
                )}
              >
                <LuGlobe size={16} />
              </div>
              <span className="text-sm">Website</span>
            </DropdownMenuItem>
          </OrgPermissionGate>
          <OrgPermissionGate permission="write" fallback="disable" asChild>
            <DropdownMenuItem
              className="flex items-center gap-2.5"
              onClick={handleCreateTag}
            >
              <div
                className={twMerge(
                  LIBRARY_ICON_BG['accent-light'],
                  LIBRARY_ICON_TEXT['accent-light'],
                  BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
                )}
              >
                <LuTag size={16} />
              </div>
              <span className="text-sm">New Tag</span>
            </DropdownMenuItem>
          </OrgPermissionGate>
          {!isLoadingPremium && canAccessPremium && (
            <>
              <DropdownMenuSeparator />
              <OrgPermissionGate permission="write" fallback="disable" asChild>
                <DropdownMenuItem
                  className="flex items-center gap-2.5"
                  onClick={handleCreateExcerpt}
                  disabled={isCreatingExcerpt}
                >
                  {isCreatingExcerpt ? (
                    <Spinner className="w-6 h-6" variant="primary-compact" />
                  ) : (
                    <div
                      className={twMerge(
                        LIBRARY_ICON_BG[
                          LIBRARY_SIDEBAR_ID_TO_COLOR['excerpts']
                        ],
                        LIBRARY_ICON_TEXT[
                          LIBRARY_SIDEBAR_ID_TO_COLOR['excerpts']
                        ],
                        BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
                      )}
                    >
                      <ExcerptsIcon size={16} />
                    </div>
                  )}
                  <span className="text-sm">New Excerpt</span>
                </DropdownMenuItem>
              </OrgPermissionGate>
            </>
          )}
          <OrgPermissionGate permission="write" fallback="disable" asChild>
            <Deprecated label="Templates" plural>
              <DropdownMenuItem
                className="flex items-center gap-2.5"
                onClick={handleCreateTemplate}
                // TODO: templates are deprecated, remove in a future release
                disabled={true}
              >
                {isCreatingTemplate ? (
                  <Spinner className="w-6 h-6" variant="primary-compact" />
                ) : (
                  <div
                    className={twMerge(
                      LIBRARY_ICON_BG[LIBRARY_SIDEBAR_ID_TO_COLOR['templates']],
                      LIBRARY_ICON_TEXT[
                        LIBRARY_SIDEBAR_ID_TO_COLOR['templates']
                      ],
                      BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
                    )}
                  >
                    <TemplatesIcon size={16} />
                  </div>
                )}
                <span className="text-sm">New Template</span>
              </DropdownMenuItem>
            </Deprecated>
          </OrgPermissionGate>
          <OrgPermissionGate permission="write" fallback="disable" asChild>
            <DropdownMenuItem
              className="flex items-center gap-2.5"
              onClick={handleUploadTemplate}
            >
              <div
                className={twMerge(
                  LIBRARY_ICON_BG[
                    LIBRARY_SIDEBAR_ID_TO_COLOR['word-templates']
                  ],
                  LIBRARY_ICON_TEXT[
                    LIBRARY_SIDEBAR_ID_TO_COLOR['word-templates']
                  ],
                  BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
                )}
              >
                <WordTemplatesIcon size={16} />
              </div>
              <span className="text-sm">New Word Template</span>
            </DropdownMenuItem>
          </OrgPermissionGate>
          <DropdownMenuSeparator />
          <OrgPermissionGate permission="write" fallback="disable" asChild>
            <DropdownMenuItem
              className="flex items-center gap-2.5"
              onClick={handleCreateCompany}
            >
              <div
                className={twMerge(
                  LIBRARY_ICON_BG[LIBRARY_SIDEBAR_ID_TO_COLOR['companies']],
                  LIBRARY_ICON_TEXT[LIBRARY_SIDEBAR_ID_TO_COLOR['companies']],
                  BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
                )}
              >
                <CompaniesIcon size={16} />
              </div>
              <span className="text-sm">New Company</span>
            </DropdownMenuItem>
          </OrgPermissionGate>
          <OrgPermissionGate permission="write" fallback="disable" asChild>
            <DropdownMenuItem
              className="flex items-center gap-2.5"
              onClick={handleCreatePerson}
            >
              <div
                className={twMerge(
                  LIBRARY_ICON_BG[LIBRARY_SIDEBAR_ID_TO_COLOR['people']],
                  LIBRARY_ICON_TEXT[LIBRARY_SIDEBAR_ID_TO_COLOR['people']],
                  BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
                )}
              >
                <PeopleIcon size={16} />
              </div>
              <span className="text-sm">New Person</span>
            </DropdownMenuItem>
          </OrgPermissionGate>
        </DropdownMenuContent>
      </DropdownMenu>
    </>
  )
}

const selectOrgCompanyProfile = (data: OrgCompanyProfileResponse) => {
  return {
    faviconUrl: data.company?.website_url
      ? getFaviconUrl(data.company.website_url, 128)
      : undefined,
    name: data.company?.name,
  }
}

const selectOrgAvatar = (data: Organization) => {
  return {
    faviconUrl: data.website_url
      ? getFaviconUrl(data.website_url, 128)
      : undefined,
    name: data.name,
  }
}

interface CompanyProfileIconProps {
  className?: string
  fallbackClassName?: string
}

export const CompanyProfileIcon: React.FC<CompanyProfileIconProps> = ({
  className,
  fallbackClassName,
}) => {
  const { activeOrgId } = useActiveOrgId()
  const { data: org, isLoading: isLoadingOrg } = useOrganization(activeOrgId, {
    select: selectOrgAvatar,
  })
  const { data: orgCompanyProfile, isLoading: isLoadingOrgCompanyProfile } =
    useOrgCompanyProfile({
      orgId: activeOrgId,
      select: selectOrgCompanyProfile,
    })

  const isLoading = isLoadingOrg || isLoadingOrgCompanyProfile

  if (isLoading) {
    return (
      <AvatarSkeleton
        width={28}
        height={28}
        className="rounded-md"
        noName={true}
      />
    )
  }

  const avatar = org?.faviconUrl ?? orgCompanyProfile?.faviconUrl
  const name = org ? org.name : (orgCompanyProfile?.name ?? 'Company Profile')

  if (avatar === undefined) {
    const Icon = LIBRARY_SIDEBAR_ID_TO_ICON['profile']
    return (
      <div
        className={twMerge(
          LIBRARY_ICON_BG[LIBRARY_SIDEBAR_ID_TO_COLOR['profile']],
          LIBRARY_ICON_TEXT[LIBRARY_SIDEBAR_ID_TO_COLOR['profile']],
          BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
          'w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0',
          fallbackClassName,
          className,
        )}
      >
        <Icon size={16} />
      </div>
    )
  }

  return (
    <div
      className={twMerge(
        // using zinc since that's the default color for the profile icon
        'w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0 bg-zinc-200/80 dark:bg-zinc-700',
        className,
      )}
    >
      <Avatar
        name={name}
        src={avatar}
        width={22}
        height={22}
        noTooltip={true}
        initialShowFallback={false}
        startWithFallback={false}
        className={avatar !== undefined ? 'rounded-[4px]' : undefined}
      />
    </div>
  )
}

interface SharepointIconProps {
  size?: number
  className?: string
  fallbackClassName?: string
}

export const SharepointIcon: React.FC<SharepointIconProps> = ({
  size = 20,
  className,
}) => {
  return (
    <div
      className={twMerge(
        LIBRARY_ICON_BG[LIBRARY_SIDEBAR_ID_TO_COLOR['sharepoint']],
        LIBRARY_ICON_TEXT[LIBRARY_SIDEBAR_ID_TO_COLOR['sharepoint']],
        BASE_NEW_BUTTON_ITEM_ICON_CLASSNAME,
        'w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0',
        className,
      )}
    >
      <BaseSharePointIcon size={size} />
    </div>
  )
}

interface SidebarProps {
  className?: string
  onUploadFile?: NewButtonProps['onUploadFile']
}

export const Sidebar: React.FC<SidebarProps> = ({
  className,
  onUploadFile,
}) => {
  const { activeCatalogSchemaId } = useActiveCatalogSchemaId()

  const { activeOrgId } = useActiveOrgId()
  const { data: org } = useOrganization(activeOrgId)

  // Fetch the org's company profile
  const { data: orgCompanyProfileId } = useOrgCompanyProfileId({
    orgId: activeOrgId,
  })

  // Get active route state from hook
  const { isProfileActive, isCompaniesActive, profileRoute } =
    useProfileSidebarState({
      orgId: activeOrgId,
      profileCompanyId: orgCompanyProfileId,
    })

  const { data: fileCount } = useOrganizationFilesCount({ orgId: activeOrgId })
  const { data: peopleCount } = usePeopleCount({ orgId: activeOrgId })
  const { data: companyCount } = useCompanyCount({ orgId: activeOrgId })
  const { data: templateCount } = useTemplateCount({ orgId: activeOrgId })
  const { data: excerptCount } = useExcerptCount({ orgId: activeOrgId })
  const { data: wordTemplateCount } = useWordTemplatesCount({
    orgId: activeOrgId,
  })
  const { data: sharepointSyncedCount } = useSharepointSyncedFilesCount({
    orgId: activeOrgId,
  })
  const { data: savedPromptCount } = usePromptCount({ orgId: activeOrgId })
  const { data: memoryCount } = useMemoryCount({ orgId: activeOrgId })

  const { data: pendingPeopleCount } = useSuggestedPeopleCount({
    orgId: activeOrgId,
  })
  const { data: pendingCompaniesCount } = useSuggestedCompaniesCount({
    orgId: activeOrgId,
  })

  // NOTE: temporarily disabled while catalog isn't getting usage
  // const { data: catalogSchemas } = useCatalogEntitySchemas({
  //   orgId: activeOrgId,
  // })
  // const { data: catalogEntriesCount } = useCatalogEntriesCount({
  //   orgId: activeOrgId,
  // })
  // const { data: pendingCatalogEntriesCount } = useSuggestedCatalogEntriesCount({
  //   orgId: activeOrgId,
  // })

  // const [importEntriesDialogOpen, setImportEntriesDialogOpen] =
  //   useState<boolean>(false)

  // const handleImportCatalog = useCallback(() => {
  //   setImportEntriesDialogOpen(true)
  // }, [setImportEntriesDialogOpen])

  const dataItems: NavigationItem[] = useMemo(() => {
    return [
      // {
      //   id: 'home',
      //   icon: LuHome,
      //   label: 'Home',
      //   count: null,
      //   color: 'zinc-light',
      //   route: `/orgs/${activeOrgId}/library`,
      // },
      {
        id: 'profile',
        icon: <CompanyProfileIcon />,
        label: 'Company Profile',
        count: null,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['profile'],
        route: profileRoute,
        isActiveOverride: isProfileActive,
      },
      {
        id: 'files',
        icon: LIBRARY_SIDEBAR_ID_TO_ICON['files'],
        label: 'Files',
        count: fileCount?.count ?? null,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['files'],
        route: `/orgs/${activeOrgId}/library/files`,
      },
      {
        id: 'sharepoint',
        icon: <SharepointIcon />,
        label: 'SharePoint',
        count: sharepointSyncedCount?.count ?? null,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['sharepoint'],
        route: `/orgs/${activeOrgId}/library/sharepoint`,
      },
    ]
  }, [
    activeOrgId,
    fileCount?.count,
    profileRoute,
    isProfileActive,
    sharepointSyncedCount?.count,
  ])

  const aiContextItems: NavigationItem[] = useMemo(() => {
    return [
      {
        id: 'prompts',
        icon: LIBRARY_SIDEBAR_ID_TO_ICON['prompts'],
        label: 'Playbooks',
        count: savedPromptCount?.count ?? null,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['prompts'],
        route: `/orgs/${activeOrgId}/library/playbooks`,
      },
      {
        id: 'memories',
        icon: LIBRARY_SIDEBAR_ID_TO_ICON['memories'],
        label: 'Memories',
        count: memoryCount?.count ?? null,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['memories'],
        route: `/orgs/${activeOrgId}/library/memories`,
      },
    ]
  }, [activeOrgId, savedPromptCount?.count, memoryCount?.count])

  const contentItems: NavigationItem[] = useMemo(() => {
    return [
      {
        id: 'excerpts',
        icon: TbBlockquote,
        label: 'Excerpts',
        count: excerptCount?.count ?? null,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['excerpts'],
        route: `/orgs/${activeOrgId}/library/excerpts`,
      },
      {
        id: 'templates',
        icon: LIBRARY_SIDEBAR_ID_TO_ICON['templates'],
        label: 'Templates',
        count: templateCount?.count ?? null,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['templates'],
        route: `/orgs/${activeOrgId}/library/templates`,
      },
      {
        id: 'word-templates',
        icon: LIBRARY_SIDEBAR_ID_TO_ICON['word-templates'],
        label: 'Word Templates',
        count: wordTemplateCount?.count ?? null,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['word-templates'],
        route: `/orgs/${activeOrgId}/library/word-templates`,
      },
    ]
  }, [
    activeOrgId,
    templateCount?.count,
    wordTemplateCount?.count,
    excerptCount?.count,
  ])

  const directoryItems: NavigationItem[] = useMemo(() => {
    return [
      {
        id: 'companies',
        icon: LIBRARY_SIDEBAR_ID_TO_ICON['companies'],
        label: 'Companies',
        count: companyCount ?? null,
        suggestionCount: pendingCompaniesCount || null,
        suggestionRoute: `/orgs/${activeOrgId}/library/companies?status=suggested`,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['companies'],
        route: `/orgs/${activeOrgId}/library/companies`,
        isActiveOverride: isCompaniesActive,
      },
      {
        id: 'people',
        icon: LIBRARY_SIDEBAR_ID_TO_ICON['people'],
        label: 'People',
        count: peopleCount ?? null,
        suggestionCount: pendingPeopleCount || null,
        suggestionRoute: `/orgs/${activeOrgId}/library/people?status=suggested`,
        color: LIBRARY_SIDEBAR_ID_TO_COLOR['people'],
        route: `/orgs/${activeOrgId}/library/people`,
      },
      // {
      //   id: 'agencies',
      //   icon: LIBRARY_SIDEBAR_ID_TO_ICON['agencies'],
      //   label: 'Agencies',
      //   count: agencyCount?.agency ?? null,
      //   color: LIBRARY_SIDEBAR_ID_TO_COLOR['agencies'],
      //   route: `/orgs/${activeOrgId}/library/agencies`,
      // },
      // {
      //   id: 'catalog',
      //   icon: LIBRARY_SIDEBAR_ID_TO_ICON['catalog'],
      //   label: 'Catalog',
      //   count: catalogEntriesCount ?? null,
      //   suggestionCount: pendingCatalogEntriesCount || null,
      //   color: LIBRARY_SIDEBAR_ID_TO_COLOR['catalog'],
      //   route: `/orgs/${activeOrgId}/library/catalog`,
      //   children: catalogSchemas?.map((schema) => ({
      //     id: schema.id,
      //     label: schema.name,
      //     route: `/orgs/${activeOrgId}/library/catalog?schema-id=${schema.id}`,
      //     count: null,
      //     active: activeCatalogSchemaId === schema.id,
      //   })),
      //   onNew: handleImportCatalog,
      // },
      // {
      //   icon: LIBRARY_SIDEBAR_ID_TO_ICON['contracts'],
      //   label: 'Contracts',
      //   count: null,
      //   color: LIBRARY_SIDEBAR_ID_TO_COLOR['contracts'],
      //   route: 'library-v2/contracts',
      // },
    ]
  }, [
    activeCatalogSchemaId,
    activeOrgId,
    peopleCount,
    companyCount,
    isCompaniesActive,
    pendingPeopleCount,
    pendingCompaniesCount,
    // catalogEntriesCount,
    // catalogSchemas,
    // pendingCatalogEntriesCount,
    // handleImportCatalog,
  ])

  const items = useMemo(
    () => [
      {
        title: 'Organization Data',
        items: dataItems,
      },
      {
        title: 'AI Context',
        items: aiContextItems,
      },
      {
        title: 'Proposal Templates',
        items: contentItems,
      },
      {
        title: 'Directory',
        items: directoryItems,
      },
    ],
    [dataItems, aiContextItems, contentItems, directoryItems],
  )

  return (
    <div
      className={twMerge(
        'w-full flex flex-col overflow-hidden h-full flex-shrink-0',
        'divide-y divide-primary-200 dark:divide-primary-800',
        'truncate',
        className,
      )}
    >
      {/* <ImportEntriesDialog
        open={importEntriesDialogOpen}
        onOpenChange={setImportEntriesDialogOpen}
      /> */}
      <div className="flex items-center px-4 h-[48px] min-h-[48px] max-h-[48px] w-full truncate">
        {org ? (
          <span className="text-md font-medium text-primary-900 dark:text-primary-100 w-full truncate">
            {org.name}
          </span>
        ) : (
          <Skeleton className="w-24 h-6" />
        )}
      </div>

      <div className="flex-1 overflow-y-auto space-y-6">
        <div className="px-3 pt-3">
          <NewButton onUploadFile={onUploadFile} />
        </div>
        {items.map((item) => {
          return (
            <div className="px-4" key={item.title}>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-primary-700 dark:text-primary-300 mb-3 uppercase tracking-wide">
                  {item.title}
                </span>
              </div>
              <div className="space-y-[5px] -mx-[6px]">
                {item.items.map((item) => {
                  const sidebarItem = <SidebarItem key={item.id} item={item} />
                  if (item.id !== 'sharepoint') {
                    return sidebarItem
                  }
                  return (
                    <OrgPermissionGate
                      key={item.id}
                      permission="read"
                      fallback="hide"
                    >
                      {sidebarItem}
                    </OrgPermissionGate>
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}
